/**
 * The floating chat pane's outer shell shared by the canvas and home/workspace
 * surfaces: the window-filling column (header above body) plus the provider
 * nest the chat body needs — `DropZoneProvider` → `MessageInputProvider` →
 * `SessionProvider`, in that order (the drop zone sits outermost so a session
 * switch doesn't unmount the droppable mid-drag).
 *
 * `sessionKey` keys the provider nest so switching sessions gives a clean
 * per-session ChatArea / composer; the workspace passes `"draft"` while the
 * window has no session yet, so attaching a file in a draft (which creates the
 * session early) doesn't remount the body and drop the upload queue.
 */

import { MessageInputProvider } from "@/context/MessageInputContext";
import { SessionProvider } from "@/context/SessionContext";
import { DropZoneProvider } from "@/dnd/OsFileDropLayer";
import type { ReactNode } from "react";

export function FloatingChatPaneShell({
  header,
  sessionKey,
  initialSessionId,
  children,
}: {
  header: ReactNode;
  /** Remount key for the provider nest (session id, or `"draft"`). */
  sessionKey: string;
  /** Session the provider nest opens on (undefined for a draft). */
  initialSessionId?: string;
  children: ReactNode;
}) {
  return (
    <div className="h-full flex flex-col bg-surface-floating-chat">
      {header}

      <DropZoneProvider
        key={sessionKey}
        className="flex-1 min-h-0 flex flex-col"
      >
        <MessageInputProvider>
          <SessionProvider initialSessionId={initialSessionId}>
            {children}
          </SessionProvider>
        </MessageInputProvider>
      </DropZoneProvider>
    </div>
  );
}
